import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  TrendingUp, MessageSquare, AlertCircle, CheckCircle, ShieldAlert,
  Download, FileText, Search, Filter, Calendar, Phone, Star,
  BarChart3, PieChart as PieIcon, Activity, Mail, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// ✅ URL del backend LexPro Studio
const BACKEND = 'https://lexprostudio.duckdns.org';

const TYPES = ['Tutti', 'Avvocato', 'Notaio', 'Consulente', 'Azienda'];
const DATERANGES = [
  { label: 'Tutti', value: 'all' },
  { label: 'Ultimo mese', value: '1m' },
  { label: 'Ultimi 3 mesi', value: '3m' },
  { label: 'Ultimi 6 mesi', value: '6m' },
];

const getSentiment = (score) => {
  if (score >= 9) return { label: 'Promotore', icon: CheckCircle, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
  if (score >= 7) return { label: 'Passivo', icon: AlertCircle, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
  return { label: 'Detrattore', icon: ShieldAlert, bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
};

const isWithinDate = (dateStr, range) => {
  if (range === 'all') return true;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now - d) / (1000 * 60 * 60 * 24);
  if (range === '1m') return diff <= 30;
  if (range === '3m') return diff <= 90;
  if (range === '6m') return diff <= 180;
  return true;
};

// Genera storico mensile dai dati reali
const buildHistory = (clients) => {
  const months = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
  const grouped = {};
  clients.forEach((c) => {
    const d = new Date(c.data || c.date);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    if (!grouped[key]) grouped[key] = { month: months[d.getMonth()], scores: [] };
    grouped[key].scores.push(c.voto ?? c.score);
  });
  return Object.values(grouped).map((g) => {
    const total = g.scores.length || 1;
    const promoters = g.scores.filter((s) => s >= 9).length;
    const detractors = g.scores.filter((s) => s <= 6).length;
    const nps = Math.round(((promoters - detractors) / total) * 100);
    return { month: g.month, nps, responses: total };
  });
};

// Normalizza record dal backend (campo "voto") al formato UI (campo "score")
const normalizeRecord = (r, idx) => ({
  id: r.id || String(idx),
  name: r.utente || 'Anonimo',
  type: r.tipo || 'Cliente',
  email: r.email || '—',
  score: r.voto ?? r.score ?? 0,
  comment: r.commento || '',
  date: (r.data || r.date || '').slice(0, 10),
  status: r.status || 'da ricontattare',
});

export default function NPSDashboard() {
  const dashboardRef = useRef(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [typeFilter, setTypeFilter] = useState('Tutti');
  const [dateRange, setDateRange] = useState('all');
  const [clientData, setClientData] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = () => {
    setLoading(true);
    setError(null);
    fetch(`${BACKEND}/api/nps`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        if (d.ok && Array.isArray(d.dati)) {
          const normalized = d.dati.map(normalizeRecord);
          setClientData(normalized);
          setHistory(buildHistory(normalized));
          setLastUpdate(new Date().toLocaleTimeString('it-IT'));
        } else {
          throw new Error(d.errore || 'Risposta non valida');
        }
      })
      .catch((e) => {
        setError(e.message);
      })
      .finally(() => setLoading(false));
  };

  // Carica dati all'avvio e ogni 60 secondi
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const filtered = useMemo(() => {
    return clientData.filter((c) => {
      const matchesSearch = (c.name + ' ' + c.comment + ' ' + c.email).toLowerCase().includes(search.toLowerCase());
      const matchesType = typeFilter === 'Tutti' || c.type === typeFilter;
      const matchesDate = isWithinDate(c.date, dateRange);
      let matchesCategory = true;
      if (category === 'promoters') matchesCategory = c.score >= 9;
      if (category === 'passives') matchesCategory = c.score >= 7 && c.score <= 8;
      if (category === 'detractors') matchesCategory = c.score <= 6;
      return matchesSearch && matchesType && matchesDate && matchesCategory;
    });
  }, [clientData, search, typeFilter, dateRange, category]);

  const stats = useMemo(() => {
    const total = filtered.length || 1;
    const promoters = filtered.filter((c) => c.score >= 9).length;
    const passives = filtered.filter((c) => c.score >= 7 && c.score <= 8).length;
    const detractors = filtered.filter((c) => c.score <= 6).length;
    const nps = Math.round(((promoters - detractors) / total) * 100);
    const avg = (filtered.reduce((s, c) => s + c.score, 0) / total).toFixed(1);
    return { total, promoters, passives, detractors, nps, avg, responseRate: clientData.length };
  }, [filtered, clientData]);

  const distribution = useMemo(() => [
    { name: 'Promotori', value: stats.promoters, color: '#10B981' },
    { name: 'Passivi', value: stats.passives, color: '#F59E0B' },
    { name: 'Detrattori', value: stats.detractors, color: '#F43F5E' },
  ], [stats]);

  const unsatisfied = useMemo(() => clientData.filter((c) => c.score <= 6 && c.status !== 'contattato'), [clientData]);

  const exportCSV = () => {
    const headers = ['ID', 'Nome', 'Tipo', 'Email', 'Voto', 'Commento', 'Data', 'Stato'];
    const rows = filtered.map((c) => [
      c.id, c.name, c.type, c.email, c.score, `"${c.comment.replace(/"/g, '""')}"`, c.date, c.status
    ]);
    const csv = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nps_feedback_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, logging: false });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
      const x = (pageWidth - imgWidth * ratio) / 2;
      const y = 10;
      pdf.addImage(imgData, 'PNG', x, y, imgWidth * ratio, imgHeight * ratio);
      pdf.save(`nps_dashboard_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) {
      console.error(e);
      window.print();
    }
  };

  const markContacted = (id) => {
    setClientData((prev) => prev.map((c) => (c.id === id ? { ...c, status: 'contattato' } : c)));
  };

  // Schermata di caricamento
  if (loading && clientData.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Caricamento dati NPS...</p>
          <p className="text-slate-400 text-sm mt-1">{BACKEND}/api/nps</p>
        </div>
      </div>
    );
  }

  // Schermata di errore
  if (error && clientData.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h2 className="text-slate-900 font-bold text-lg mb-2">Errore di connessione</h2>
          <p className="text-slate-500 text-sm mb-4">{error}</p>
          <p className="text-slate-400 text-xs mb-6">Backend: {BACKEND}/api/nps</p>
          <button onClick={fetchData} className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition flex items-center gap-2 mx-auto">
            <RefreshCw className="w-4 h-4" /> Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900 leading-tight">NPS Dashboard</h1>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                LexPro Studio {lastUpdate && `· Aggiornato: ${lastUpdate}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchData} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 transition" title="Aggiorna dati">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={exportCSV} className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 border border-slate-200 transition">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={exportPDF} className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 shadow-lg shadow-slate-900/20 transition">
              <FileText className="w-4 h-4" /> PDF
            </button>
          </div>
        </div>
      </header>

      {/* Banner errore non bloccante */}
      {error && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 text-center text-xs text-amber-700 flex items-center justify-center gap-2">
          <AlertCircle className="w-3.5 h-3.5" />
          Errore aggiornamento: {error} — visualizzando ultimi dati disponibili
          <button onClick={fetchData} className="underline font-medium">Riprova</button>
        </div>
      )}

      <main ref={dashboardRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-bl-full -mr-4 -mt-4" />
            <div className="relative">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">NPS Globale</p>
              <div className="mt-2 flex items-end gap-2">
                <span className={`text-4xl font-extrabold tracking-tight ${stats.nps >= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                  {stats.nps > 0 ? `+${stats.nps}` : stats.nps}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">{stats.promoters} promotori · {stats.detractors} detrattori</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-50 rounded-bl-full -mr-4 -mt-4" />
            <div className="relative">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Feedback Totali</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-emerald-600">{stats.responseRate}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">Valutazioni ricevute</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
            <div className="absolute top-0 right-0 w-24 h-24 bg-violet-50 rounded-bl-full -mr-4 -mt-4" />
            <div className="relative">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Filtrati</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-violet-600">{stats.total}</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">Con i filtri attivi</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="relative overflow-hidden rounded-2xl bg-white p-6 shadow-sm border border-slate-200">
            <div className="absolute top-0 right-0 w-24 h-24 bg-amber-50 rounded-bl-full -mr-4 -mt-4" />
            <div className="relative">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Voto Medio</p>
              <div className="mt-2 flex items-end gap-2">
                <span className="text-4xl font-extrabold tracking-tight text-amber-600">{stats.avg}</span>
                <span className="text-xs text-slate-300 mb-1">/ 10</span>
              </div>
              <div className="flex gap-0.5 mt-1">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} className={`w-3 h-3 ${s <= Math.round(stats.avg / 2) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filtri */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, commento o email..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 px-2 py-1.5">
                <Filter className="w-3.5 h-3.5 text-slate-400" />
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 px-2 py-1.5">
                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} className="bg-transparent text-sm font-medium text-slate-700 focus:outline-none">
                  {DATERANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            {[
              { key: 'all', label: 'Tutti', count: clientData.length, color: 'bg-slate-100 text-slate-700 border-slate-200', active: 'bg-slate-900 text-white border-slate-900' },
              { key: 'promoters', label: 'Promotori', count: clientData.filter((c) => c.score >= 9).length, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', active: 'bg-emerald-600 text-white border-emerald-600' },
              { key: 'passives', label: 'Passivi', count: clientData.filter((c) => c.score >= 7 && c.score <= 8).length, color: 'bg-amber-50 text-amber-700 border-amber-200', active: 'bg-amber-500 text-white border-amber-500' },
              { key: 'detractors', label: 'Detrattori', count: clientData.filter((c) => c.score <= 6).length, color: 'bg-rose-50 text-rose-700 border-rose-200', active: 'bg-rose-600 text-white border-rose-600' },
            ].map((chip) => (
              <button
                key={chip.key}
                onClick={() => setCategory(chip.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${category === chip.key ? chip.active : chip.color}`}
              >
                {chip.label} <span className="opacity-70 ml-1">{chip.count}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Grafici */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-indigo-500" /> Andamento NPS
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">Evoluzione del punteggio negli ultimi mesi</p>
              </div>
            </div>
            <div className="h-72">
              {history.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorNps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366F1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366F1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                    <Area type="monotone" dataKey="nps" stroke="#6366F1" strokeWidth={3} fill="url(#colorNps)" dot={{ r: 4, fill: '#6366F1', stroke: '#fff', strokeWidth: 2 }} activeDot={{ r: 6 }} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                  Dati insufficienti per il grafico storico
                </div>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="mb-4">
              <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <PieIcon className="w-4 h-4 text-violet-500" /> Distribuzione
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">Composizione attuale del punteggio</p>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={distribution} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={4} dataKey="value" stroke="none">
                    {distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
                  <Legend verticalAlign="bottom" height={36} iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '12px', color: '#64748b' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {distribution.map((d) => (
                <div key={d.name} className="text-center">
                  <p className="text-lg font-bold" style={{ color: d.color }}>{d.value}</p>
                  <p className="text-[10px] text-slate-400 uppercase font-semibold">{d.name}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Lista feedback + Da ricontattare */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-slate-500" /> Recensioni & Feedback
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">{filtered.length} risultati visualizzati</p>
              </div>
              <div className="flex gap-2">
                <button onClick={exportCSV} className="sm:hidden flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 border border-slate-200">
                  <Download className="w-3.5 h-3.5" /> CSV
                </button>
              </div>
            </div>
            <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
              <AnimatePresence>
                {filtered.map((item) => {
                  const sentiment = getSentiment(item.score);
                  const Icon = sentiment.icon;
                  return (
                    <motion.div
                      key={item.id}
                      layout
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      className="p-5 hover:bg-slate-50/60 transition-colors flex flex-col sm:flex-row sm:items-start gap-4"
                    >
                      <div className={`shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold shadow-sm ${sentiment.bg} ${sentiment.text}`}>
                        {item.score}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-slate-900">{item.name}</span>
                          {item.type !== 'Cliente' && (
                            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{item.type}</span>
                          )}
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full border ${sentiment.bg} ${sentiment.text} ${sentiment.border}`}>
                            <Icon className="w-3 h-3" /> {sentiment.label}
                          </span>
                        </div>
                        {item.comment && <p className="text-sm text-slate-600 leading-relaxed">{item.comment}</p>}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                          {item.email !== '—' && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {item.email}</span>}
                          {item.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {item.date}</span>}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
              {filtered.length === 0 && !loading && (
                <div className="p-12 text-center text-slate-400 text-sm">
                  <Search className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                  {clientData.length === 0 ? 'Nessuna valutazione ricevuta ancora.' : 'Nessun risultato corrisponde ai filtri selezionati.'}
                </div>
              )}
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-gradient-to-br from-rose-600 to-rose-700 rounded-2xl shadow-xl shadow-rose-200 text-white overflow-hidden flex flex-col">
            <div className="p-5 border-b border-rose-500/30">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-sm font-bold">Da Ricontattare</h2>
              </div>
              <p className="text-xs text-rose-100">{unsatisfied.length} clienti insoddisfatti richiedono attenzione</p>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 max-h-[500px]">
              <AnimatePresence>
                {unsatisfied.map((u) => (
                  <motion.div
                    key={u.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 hover:bg-white/15 transition"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-bold truncate">{u.name}</p>
                        {u.email !== '—' && <p className="text-xs text-rose-200 mt-0.5 truncate">{u.email}</p>}
                        {u.comment && <p className="text-xs text-white/80 mt-1.5 leading-snug line-clamp-2">"{u.comment}"</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/20">Voto {u.score}</span>
                          {u.date && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/20">{u.date}</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => markContacted(u.id)}
                        className="shrink-0 w-8 h-8 rounded-lg bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
                        title="Segna come contattato"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
              {unsatisfied.length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-10 h-10 mx-auto mb-3 text-white/40" />
                  <p className="text-sm font-medium text-white/70">Nessun cliente insoddisfatto da ricontattare</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-rose-500/30 bg-rose-800/20">
              <button onClick={() => setCategory('detractors')} className="w-full py-2.5 rounded-xl bg-white text-rose-600 text-sm font-bold hover:bg-rose-50 transition shadow-lg">
                Vedi tutti i detrattori
              </button>
            </div>
          </motion.div>
        </div>

        <div className="text-center text-xs text-slate-400 pb-8">
          LexPro Studio · NPS Dashboard v2.0 · Dati live da {BACKEND}
        </div>
      </main>
    </div>
  );
}
